import React, { useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { Button, Col, Row, Input, message, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { useHistory, useParams } from 'react-router-dom';

import { PageTitle } from '../../../components/PageTitle';
import { CanvasObject } from './types';
import { CanvasBuilderContainer } from './styles';
import { useCanvasInteractions } from './hooks/useCanvasInteractions';
import { PropertiesPanel } from './components/PropertiesPanel';
import { CanvasObjectComponent } from './components/CanvasObject';
import { PreviewModal } from './components/PreviewModal';
import { ImageUploadModal } from './components/ImageUploadModal';
import { Toolbar } from './components/Toolbar';
import { ConnectorArrow } from './components/ConnectorArrow';
import { createObject, captureCanvas, getCanvasCoordinates } from './utils';
import { SavePreviewModal } from './components/SavePreviewModal';
import { TOOLS } from './constants';
import { useRootStore } from '../../../context/storeContext';

export const CanvasBuilder = observer(() => {
    const history = useHistory();
    const canvasRef = useRef<HTMLDivElement>(null);
    const { templatesStore, transportLayer } = useRootStore();

    // State
    const [objects, setObjects] = useState<CanvasObject[]>([]);
    const [selectedTool, setSelectedTool] = useState<string>('select');
    const [selectedObjects, setSelectedObjects] = useState<string[]>([]);
    const [templateName, setTemplateName] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [showImageUpload, setShowImageUpload] = useState(false);
    const [pendingImagePosition, setPendingImagePosition] = useState<{ x: number; y: number } | null>(null);
    const [draggedTool, setDraggedTool] = useState<string | null>(null);
    const [dragPreview, setDragPreview] = useState<{ x: number; y: number; tool: ReactNode } | null>(null);
    const [endpointDrag, setEndpointDrag] = useState<{
        objectId: string;
        endpoint: 'start' | 'end' | 'breakpoint';
        startMouseX: number;
        startMouseY: number;
        startPointX: number;
        startPointY: number;
    } | null>(null);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
    const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
    const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
    const [showSavePreview, setShowSavePreview] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [templatePreviewUrl, setTemplatePreviewUrl] = useState<string | null>(null);
    const [isBaseTemplate, setIsBaseTemplate] = useState(false);

    // Custom hook for canvas interactions
    const {
        selectionBox,
        handleCanvasClick,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleObjectMouseDown,
        handleObjectMouseMove,
        handleResizeStart,
        duplicateSelectedObjects,
        deleteSelectedObjects,
    } = useCanvasInteractions(objects, setObjects, selectedObjects, setSelectedObjects, canvasRef);

    const canvasWrapperRef = useRef<HTMLDivElement>(null);
    const [canvasScale, setCanvasScale] = useState(1);

    useEffect(() => {
        if (!canvasWrapperRef.current) return;

        const observer = new ResizeObserver((entries) => {
            const { width, height } = entries[0].contentRect;
            const PADDING = 20;
            const scaleX = (width - PADDING) / 1920;
            const scaleY = (height - PADDING) / 1080;
            const scale = Math.min(scaleX, scaleY, 1.2);
            setCanvasScale(Math.max(0.1, scale));
        });

        observer.observe(canvasWrapperRef.current);
        return () => observer.disconnect();
    }, []);

    // Handlers
    const handleToolSelect = (tool: string) => {
        setSelectedTool(tool);
    };

    // Drag and Drop handlers
    const handleToolDragStart = (e: React.DragEvent, toolKey: string) => {
        if (toolKey === 'select') return; // Don't allow dragging select tool

        setDraggedTool(toolKey);
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('text/plain', toolKey);

        // Create invisible drag image to hide default browser drag image
        const dragImage = document.createElement('div');
        dragImage.style.position = 'absolute';
        dragImage.style.top = '-1000px';
        dragImage.style.width = '1px';
        dragImage.style.height = '1px';
        dragImage.style.opacity = '0';
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 0, 0);
        setTimeout(() => document.body.removeChild(dragImage), 0);

        // Initialize drag preview
        const tool = TOOLS.find(t => t.key === toolKey);
        if (tool) {
            setDragPreview({ x: e.clientX, y: e.clientY, tool: tool.icon });
        }
    };

    const handleToolDragEnd = () => {
        setDraggedTool(null);
        setDragPreview(null);
    };

    const handleToolDrag = (e: React.DragEvent) => {
        // Update drag preview position during drag
        if (dragPreview && e.clientX !== 0 && e.clientY !== 0) {
            setDragPreview(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
        }
    };

    const handleCanvasDragOver = (e: React.DragEvent) => {
        if (draggedTool) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    const handleCanvasDrop = (e: React.DragEvent) => {
        e.preventDefault();

        if (!draggedTool || !canvasRef.current) return;

        const { x, y } = getCanvasCoordinates(e, canvasRef);

        if (draggedTool === 'image') {
            // Handle image tool specially
            setPendingImagePosition({ x: x - 75, y: y - 75 });
            setShowImageUpload(true);
        } else {
            // Create object directly for other tools
            const newObject = createObject(draggedTool, x - 50, y - 25);
            setObjects(prev => [...prev, newObject]);
            setSelectedObjects([newObject.id]);
        }

        setDraggedTool(null);
    };

    const handleImageToolClick = (x: number, y: number) => {
        setPendingImagePosition({ x, y });
        setShowImageUpload(true);
    };

    const handleImageUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            if (e.target?.result && pendingImagePosition) {
                const imageUrl = e.target.result as string;
                const newObject = createObject('image', pendingImagePosition.x, pendingImagePosition.y);

                // Type guard to ensure it's an ImageObject
                if (newObject.type === 'image') {
                    newObject.imageUrl = imageUrl;
                    newObject.imagePath = file.name;
                }

                setObjects(prev => [...prev, newObject]);
                setSelectedObjects([newObject.id]);
                setSelectedTool('select'); // Auto-switch to select after placing image
                setShowImageUpload(false);
                setPendingImagePosition(null);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleImageUploadCancel = () => {
        setShowImageUpload(false);
        setPendingImagePosition(null);
        setSelectedTool('select'); // Auto-switch to select when canceling
    };

    // Connector endpoint drag handlers
    const handleEndpointDragStart = (e: React.MouseEvent, objectId: string, endpoint: 'start' | 'end' | 'breakpoint') => {
        e.stopPropagation();
        const obj = objects.find((o) => o.id === objectId);
        if (!obj || obj.type !== 'arrow') return; // type is 'arrow' now

        setEndpointDrag({
            objectId,
            endpoint,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startPointX: endpoint === 'start' ? (obj.position?.x ?? 0) :
                endpoint === 'end' ? (obj.endPosition?.x ?? obj.size?.width) :
                    (obj.breakpointX ?? ((obj.position?.x ?? 0) + (obj.endPosition?.x ?? obj.size?.width)) / 2),
            startPointY: endpoint === 'start' ? (obj.position?.y ?? 0) :
                endpoint === 'end' ? (obj.endPosition?.y ?? obj.size?.height) :
                    (obj.breakpointY ?? ((obj.position?.y ?? 0) + (obj.endPosition?.y ?? obj.size?.height)) / 2),
        });
    };

    const handleEndpointDragMove = (e: React.MouseEvent) => {
        if (!endpointDrag) return;

        const deltaX = e.clientX - endpointDrag.startMouseX;
        const deltaY = e.clientY - endpointDrag.startMouseY;

        setObjects((prev) =>
            prev.map((obj) => {
                if (obj.id !== endpointDrag.objectId || obj.type !== 'arrow') return obj;

                if (endpointDrag.endpoint === 'start') {
                    return {
                        ...obj,
                        position: {
                            x: endpointDrag.startPointX + deltaX,
                            y: endpointDrag.startPointY + deltaY
                        }
                    };
                } else if (endpointDrag.endpoint === 'end') {
                    return {
                        ...obj,
                        endPosition: {
                            x: endpointDrag.startPointX + deltaX,
                            y: endpointDrag.startPointY + deltaY
                        }
                    };
                } else if (endpointDrag.endpoint === 'breakpoint') {
                    return {
                        ...obj,
                        // Breakpoint props might need to be added to types if not there, or handled custom
                        breakpointX: endpointDrag.startPointX + deltaX,
                        breakpointY: endpointDrag.startPointY + deltaY,
                    };
                }
                return obj;
            })
        );
    };

    const handleEndpointDragEnd = () => {
        setEndpointDrag(null);
    };

    const updateSelectedObjects = (updates: Partial<CanvasObject>) => {
        setObjects(prev => prev.map(obj => {
            if (!selectedObjects.includes(obj.id)) return obj;
            // Merge updates while preserving the object type
            return { ...obj, ...updates } as CanvasObject;
        }));
    };

    const handleObjectSelect = (objectId: string) => {
        setSelectedObjects([objectId]);
    };

    const handleObjectsReorder = (newObjects: CanvasObject[]) => {
        setObjects(newObjects);
    };

    const { templateId } = useParams<{ templateId: string }>();

    // Load existing template if templateId is present
    useEffect(() => {
        if (templateId) {
            const loadTemplate = async () => {
                setIsLoadingTemplate(true);
                try {
                    await templatesStore.fetchTemplates();
                    const template = templatesStore.templates.find(t => t.id === templateId);
                    if (template) {
                        setTemplateName(template.title);
                        setTemplatePreviewUrl(template.previewUrl || null);
                        setIsBaseTemplate(template.tenantId === 'guided-doing');
                        if (template.objects) {
                            // Cast the objects to CanvasObject[] ensuring types match
                            setObjects(template.objects as unknown as CanvasObject[]);
                        }
                    }
                } finally {
                    setIsLoadingTemplate(false);
                }
            };
            loadTemplate();
        }
    }, [templateId, templatesStore]);

    // Forcibly inject fonts.css into the DOM from public directory
    useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        // Base correctly depending on dev vs prod via PUBLIC_URL if possible,
        // Using /fonts/fonts.css is standard standard relative from root
        link.href = '/fonts/fonts.css';
        document.head.appendChild(link);
        return () => {
            document.head.removeChild(link);
        }
    }, []);

    const handleSaveTemplate = async () => {
        if (!templateName.trim()) {
            message.error('Please enter a template name');
            return;
        }

        if (objects.length === 0) {
            message.error('Please add some objects to the canvas');
            return;
        }

        const hideLoading = message.loading('Capturing canvas preview...', 0);

        try {
            // 1. Deselect all objects for a clean screenshot
            setSelectedObjects([]);

            // Wait a bit for the UI to update (remove selection handles)
            await new Promise(resolve => setTimeout(resolve, 500));

            // 2. Capture canvas
            if (canvasRef.current) {
                const blob = await captureCanvas(canvasRef.current);
                if (blob && blob.size > 0) {
                    setCapturedBlob(blob);
                    const url = URL.createObjectURL(blob);
                    setCapturedImageUrl(url);
                    setShowSavePreview(true);
                } else {
                    message.error('Failed to capture canvas preview');
                }
            }
        } catch (error) {
            message.error('An error occurred while capturing the template');
            console.error('Capture template error:', error);
        } finally {
            hideLoading();
        }
    };

    const uploadImageObjects = async (tmplId: string, objs: CanvasObject[]): Promise<CanvasObject[]> => {
        return Promise.all(objs.map(async (obj) => {
            if (obj.type !== 'image') return obj;
            const imageObj = obj as any;
            const url: string = imageObj.imageUrl || '';
            if (!url.startsWith('data:')) return obj;

            const mimeMatch = url.match(/^data:([^;]+);base64,/);
            const mime = mimeMatch ? mimeMatch[1] : 'image/png';
            const ext = mime.split('/')[1] || 'png';
            const base64Data = url.split(',')[1];
            const bytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
            const file = new File([bytes], `${obj.id}.${ext}`, { type: mime });

            const uploadResult = await transportLayer.uploadTemplateFile(tmplId, obj.id, file);
            if (uploadResult.succeeded && uploadResult.data) {
                return { ...obj, imageUrl: uploadResult.data.cdnUrl || uploadResult.data.fileUrl || url } as CanvasObject;
            }
            return obj;
        }));
    };

    const handleFinalSave = async () => {
        if (!capturedBlob) return;

        setIsSaving(true);
        const hideLoading = message.loading(templateId ? 'Updating template...' : 'Saving template...', 0);

        try {
            let previewUrl = '';

            // 1. Prepare objects - remove any properties not supported by the backend
            const cleanedObjects = objects.map(obj => {
                const newObj = { ...obj } as any;
                // Remove properties not in the DTO
                delete newObj.rotation;

                Object.keys(newObj).forEach(key => {
                    if (newObj[key] === null || newObj[key] === undefined) {
                        delete newObj[key];
                    }
                });
                return newObj as CanvasObject;
            });

            console.log('Cleaned objects for save:', cleanedObjects);

            let result;
            const isUpdatingExisting = !!templateId && !isBaseTemplate;

            if (isUpdatingExisting) {
                // Existing template: Upload images and preview first, then update
                const file = new File([capturedBlob], 'preview.png', { type: 'image/png' });
                console.log('Uploading preview for existing template:', templateId);
                const [uploadResult, resolvedObjects] = await Promise.all([
                    transportLayer.uploadTemplateFile(templateId, 'preview', file),
                    uploadImageObjects(templateId, cleanedObjects),
                ]);
                console.log('Upload result:', uploadResult);

                if (uploadResult.succeeded && uploadResult.data) {
                    previewUrl = uploadResult.data.cdnUrl || uploadResult.data.fileUrl || '';
                    console.log('New preview URL:', previewUrl);
                    result = await templatesStore.updateCanvasTemplate(templateId, templateName, resolvedObjects, previewUrl);
                } else {
                    result = uploadResult;
                }
            } else {
                // New template: Create first to obtain an ID
                console.log('Creating new template:', templateName);
                result = await templatesStore.createCanvasTemplate(templateName, cleanedObjects);
                console.log('Create result:', result);

                if (result.succeeded && result.data) {
                    const newTemplateId = result.data;
                    const file = new File([capturedBlob], 'preview.png', { type: 'image/png' });
                    console.log('Uploading preview and images for new template:', newTemplateId);
                    const [uploadResult, resolvedObjects] = await Promise.all([
                        transportLayer.uploadTemplateFile(newTemplateId, 'preview', file),
                        uploadImageObjects(newTemplateId, cleanedObjects),
                    ]);
                    console.log('Upload result:', uploadResult);

                    if (uploadResult.succeeded && uploadResult.data) {
                        const finalUrl = uploadResult.data.cdnUrl || uploadResult.data.fileUrl || '';
                        console.log('Final preview URL:', finalUrl);
                        result = await templatesStore.updateCanvasTemplate(newTemplateId, templateName, resolvedObjects, finalUrl);
                    } else if (uploadResult.failed) {
                        // Ensure we capture the failure from upload
                        result = uploadResult as any;
                    }
                }
            }

            console.log('Final result after all steps:', result);

            if (result && result.succeeded) {
                message.success(`Canvas template ${isUpdatingExisting ? 'updated' : 'saved'} successfully!`);
                setShowSavePreview(false);
                history.push('/organization-templates');
            } else if (result) {
                console.error('Save failed details:', result);
                message.error(result.message || `Failed to ${templateId ? 'update' : 'save'} canvas template`);
            }
        } catch (error) {
            message.error('An error occurred while saving the template');
            console.error('Final save error exception:', error);
        } finally {
            hideLoading();
            setIsSaving(false);
        }
    };

    const onBack = useCallback(() => {
        history.goBack();
    }, [history]);

    const selectedObject = selectedObjects.length === 1 ? objects.find(obj => obj.id === selectedObjects[0]) || null : null;

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Delete' && selectedObjects.length > 0) {
                deleteSelectedObjects();
            }
            if (e.key === 'd' && (e.ctrlKey || e.metaKey) && selectedObjects.length > 0) {
                e.preventDefault();
                duplicateSelectedObjects();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedObjects, deleteSelectedObjects, duplicateSelectedObjects]);

    return (
        <>
            <CanvasBuilderContainer >
                <Col span={22}>
                    <div className="header-section">
                        <Row justify="space-between" align="middle">
                            <PageTitle title="Canvas Template Builder" />
                            <div className="header-buttons">
                                <Input
                                    className="template-name-input"
                                    placeholder="Enter template name"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    style={{ width: 250 }}
                                />
                                <Button onClick={() => setShowPreview(true)} disabled={objects.length === 0}>
                                    Preview
                                </Button>
                                <Button onClick={onBack}>
                                    Cancel
                                </Button>
                                <Button type="primary" onClick={handleSaveTemplate}>
                                    {templateId ? 'Update Template' : 'Save Template'}
                                </Button>
                            </div>
                        </Row>
                    </div>

                    <div className="canvas-container" style={{ position: 'relative', height: 'calc(100vh - 120px)', width: '100%' }}>
                        <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                            <Toolbar
                                TOOLS={TOOLS}
                                selectedTool={selectedTool}
                                objects={objects}
                                selectedObjects={selectedObjects}
                                draggedTool={draggedTool}
                                onToolSelect={handleToolSelect}
                                onToolDragStart={handleToolDragStart}
                                onToolDrag={handleToolDrag}
                                onToolDragEnd={handleToolDragEnd}
                                onDuplicate={duplicateSelectedObjects}
                                onDelete={deleteSelectedObjects}
                            />
                        </div>

                        <div className="canvas-area">
                            <div className="canvas-wrapper" ref={canvasWrapperRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                                    <div
                                        ref={canvasRef}
                                        className="canvas"
                                        onClick={(e) => handleCanvasClick(e, selectedTool, handleImageToolClick, setSelectedTool)}
                                        onMouseDown={(e) => handleMouseDown(e, selectedTool)}
                                        onMouseMove={(e) => {
                                            //only handle mouse move 
                                            handleMouseMove(e);
                                            handleObjectMouseMove(e);
                                            if (endpointDrag) handleEndpointDragMove(e);
                                        }}
                                        onMouseUp={() => {
                                            handleMouseUp();
                                            handleEndpointDragEnd();
                                        }}
                                        onMouseLeave={handleEndpointDragEnd}
                                        onDragOver={handleCanvasDragOver}
                                        onDrop={handleCanvasDrop}
                                        style={{
                                            border: draggedTool ? '2px dashed #1890ff' : undefined,
                                            backgroundColor: draggedTool ? 'rgba(24, 144, 255, 0.05)' : undefined,
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: `translate(-50%, -50%) scale(${canvasScale})`,
                                            transformOrigin: 'center center'
                                        }}
                                    >
                                        {isLoadingTemplate ? (
                                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                                <Spin size="large" tip="Loading template objects..." />
                                            </div>
                                        ) : (
                                            <>
                                                {objects.map(obj =>
                                                    obj.type === 'arrow' ? (
                                                        <ConnectorArrow
                                                            key={obj.id}
                                                            obj={obj}
                                                            isSelected={selectedObjects.includes(obj.id)}
                                                            onEndpointDrag={handleEndpointDragStart}
                                                            onMouseDown={handleObjectMouseDown}
                                                        />
                                                    ) : (
                                                        <CanvasObjectComponent
                                                            key={obj.id}
                                                            obj={obj}
                                                            isSelected={selectedObjects.includes(obj.id)}
                                                            isMultiSelected={selectedObjects.length > 1 && selectedObjects.includes(obj.id)}
                                                            onMouseDown={handleObjectMouseDown}
                                                            onMouseMove={() => { }}
                                                            onResizeStart={handleResizeStart}
                                                        />
                                                    )
                                                )}
                                            </>
                                        )}

                                        {selectionBox && (
                                            <div
                                                className="selection-box"
                                                style={{
                                                    left: Math.min(selectionBox.startX, selectionBox.endX),
                                                    top: Math.min(selectionBox.startY, selectionBox.endY),
                                                    width: Math.abs(selectionBox.endX - selectionBox.startX),
                                                    height: Math.abs(selectionBox.endY - selectionBox.startY),
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                        <PropertiesPanel
                            objects={objects}
                            selectedObjects={selectedObjects}
                            selectedObject={selectedObject}
                            onUpdateObjects={updateSelectedObjects}
                            onReorderObjects={handleObjectsReorder}
                            onSelectObject={handleObjectSelect}
                        />
                    </div>
                </Col>

                <PreviewModal
                    visible={showPreview}
                    templateName={templateName}
                    objects={objects}
                    onClose={() => setShowPreview(false)}
                    onSave={handleSaveTemplate}
                />

                <ImageUploadModal
                    visible={showImageUpload}
                    onUpload={handleImageUpload}
                    onCancel={handleImageUploadCancel}
                />

                {/* Drag Preview */}
                {dragPreview && (
                    <div
                        style={{
                            position: 'fixed',
                            left: dragPreview.x - 20,
                            top: dragPreview.y - 20,
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#1890ff',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            pointerEvents: 'none',
                            zIndex: 9999,
                            transform: 'scale(1.1)',
                            transition: 'none'
                        }}
                    >
                        {dragPreview.tool}
                    </div>
                )}
                <SavePreviewModal
                    visible={showSavePreview}
                    imageUrl={capturedImageUrl}
                    previewUrl={templatePreviewUrl}
                    onCancel={() => {
                        setShowSavePreview(false);
                        if (capturedImageUrl) URL.revokeObjectURL(capturedImageUrl);
                        setCapturedImageUrl(null);
                        setCapturedBlob(null);
                    }}
                    onUpload={handleFinalSave}
                    isUpdating={!!templateId}
                    isBaseTemplate={isBaseTemplate}
                />
            </CanvasBuilderContainer>
        </>
    )
});